/**
 * InviteModal — Invite user dialog
 *
 * Two-state modal:
 *  1. Form: role selection (chips), access level, email, name, expiry, custom message
 *  2. Success: shows code, copy buttons, email sent indicator
 *
 * Based on OnMove-Admin InviteModal with Kadmin styling.
 */

import { useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Autocomplete,
  Slider,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LinkIcon from "@mui/icons-material/Link";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import KDButton from "components/KDButton";
import { useMaterialUIController } from "context";
import { useT } from "context/I18nContext";
import useInviteCodes from "hooks/useInviteCodes";
import useRoles from "hooks/useRoles";
import logger from "services/logger";

// Access levels per role — fallback if role doc doesn't have them
const DEFAULT_ACCESS_LEVELS = {
  admin: ["superadmin", "platformadmin", "standard", "readonly"],
  user: ["standard", "readonly"],
};

const ROLE_COLORS = {
  admin: { bg: "rgba(239,68,68,0.15)", color: "#FCA5A5", border: "rgba(239,68,68,0.3)" },
  user: { bg: "rgba(59,130,246,0.15)", color: "#93C5FD", border: "rgba(59,130,246,0.3)" },
};

function InviteModal({ open, onClose, onInviteCreated }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const t = useT();
  const { createInvite } = useInviteCodes();
  const { roles: availableRoles } = useRoles({ autoFetch: true });

  // Form state
  const [step, setStep] = useState("form"); // "form" | "success"
  const [selectedRole, setSelectedRole] = useState("user");
  const [accessLevel, setAccessLevel] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [sendEmail, setSendEmail] = useState(true);
  const [customMessage, setCustomMessage] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Success data
  const [createdInvite, setCreatedInvite] = useState(null);

  // Available role names
  const roleNames = useMemo(
    () => availableRoles.map((r) => r.name || r.id),
    [availableRoles]
  );

  // Access level options for selected role
  const accessLevelOptions = useMemo(() => {
    const role = availableRoles.find((r) => (r.name || r.id) === selectedRole);
    if (role?.accessLevels) return Object.keys(role.accessLevels);
    return DEFAULT_ACCESS_LEVELS[selectedRole] || ["standard", "readonly"];
  }, [selectedRole, availableRoles]);

  // Reset form
  const resetForm = useCallback(() => {
    setStep("form");
    setSelectedRole("user");
    setAccessLevel("");
    setEmail("");
    setFirstName("");
    setLastName("");
    setExpiresInDays(7);
    setSendEmail(true);
    setCustomMessage("");
    setError(null);
    setCreatedInvite(null);
    setCodeCopied(false);
    setLinkCopied(false);
  }, []);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // When role changes, reset access level
  const handleRoleChange = (role) => {
    setSelectedRole(role);
    setAccessLevel("");
  };

  // Create invite
  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const invite = await createInvite({
        email: email.trim() || undefined,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        role: selectedRole,
        accessLevel: accessLevel || undefined,
        expiresInDays,
        sendEmail: sendEmail && !!email.trim(),
        message: customMessage.trim() || undefined,
      });
      setCreatedInvite(invite);
      setStep("success");
      if (onInviteCreated) onInviteCreated(invite);
      logger.info("[InviteModal]", `Invite code created: ${invite.code}`);
    } catch (err) {
      logger.error("[InviteModal]", "Create failed:", err.message);
      setError(err.message || "Failed to create invite");
    } finally {
      setCreating(false);
    }
  };

  const handleCopyCode = () => {
    if (createdInvite?.code) {
      navigator.clipboard.writeText(createdInvite.code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const handleCopyLink = () => {
    if (createdInvite?.code) {
      const url = `${window.location.origin}/signup?invite=${createdInvite.code}`;
      navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  // Styling
  const paperSx = {
    background: darkMode
      ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
      : "linear-gradient(135deg, #fafafa 0%, #f0f0f5 100%)",
    border: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
    borderRadius: "20px",
    maxWidth: 520,
    width: "100%",
    overflow: "hidden",
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: darkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
      borderRadius: "10px",
      color: darkMode ? "white" : undefined,
      "& fieldset": { borderColor: darkMode ? "rgba(255,255,255,0.12)" : undefined },
      "&:hover fieldset": { borderColor: darkMode ? "rgba(255,255,255,0.3)" : undefined },
      "&.Mui-focused fieldset": { borderColor: "primary.main" },
    },
    "& .MuiInputLabel-root": { color: darkMode ? "rgba(255,255,255,0.6)" : undefined },
    "& .MuiOutlinedInput-input": { color: darkMode ? "white" : undefined },
  };

  const labelSx = {
    color: darkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
    fontSize: "0.75rem",
    fontWeight: 600,
    mb: 0.5,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{ sx: paperSx }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #0D9488 0%, #5EEAD4 100%)",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
          px: 3,
        }}
      >
        <KDBox display="flex" alignItems="center" gap={1}>
          <PersonAddIcon />
          <KDTypography variant="h6" fontWeight="bold" color="white">
            {step === "form" ? t("invite.title") : t("invite.success")}
          </KDTypography>
        </KDBox>
        <IconButton onClick={handleClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {step === "form" ? (
          <>
            {/* Role Selection */}
            <KDBox mb={3}>
              <KDTypography sx={labelSx}>{t("invite.selectRole")}</KDTypography>
              <KDBox display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                {roleNames.map((role) => {
                  const colors = ROLE_COLORS[role] || { bg: "rgba(13,148,136,0.15)", color: "#5EEAD4", border: "rgba(13,148,136,0.3)" };
                  const isSelected = selectedRole === role;
                  return (
                    <Chip
                      key={role}
                      label={role.charAt(0).toUpperCase() + role.slice(1)}
                      clickable
                      onClick={() => handleRoleChange(role)}
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        px: 1,
                        py: 2.5,
                        backgroundColor: isSelected ? colors.bg : "transparent",
                        color: isSelected ? colors.color : darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)",
                        border: `1.5px solid ${isSelected ? colors.border : darkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
                        "&:hover": {
                          backgroundColor: colors.bg,
                          color: colors.color,
                        },
                      }}
                    />
                  );
                })}
              </KDBox>
            </KDBox>

            {/* Access Level */}
            <KDBox mb={3}>
              <KDTypography sx={labelSx}>{t("invite.accessLevel")}</KDTypography>
              <Autocomplete
                value={accessLevel || null}
                options={accessLevelOptions}
                onChange={(_e, val) => setAccessLevel(val || "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={t("invite.accessLevelPlaceholder")}
                    sx={inputSx}
                  />
                )}
              />
            </KDBox>

            <Divider sx={{ my: 2, borderColor: darkMode ? "rgba(255,255,255,0.08)" : undefined }} />

            {/* Name fields */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={6}>
                <KDTypography sx={labelSx}>{t("invite.firstName")}</KDTypography>
                <TextField
                  fullWidth
                  size="small"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t("invite.firstNamePlaceholder")}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={6}>
                <KDTypography sx={labelSx}>{t("invite.lastName")}</KDTypography>
                <TextField
                  fullWidth
                  size="small"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t("invite.lastNamePlaceholder")}
                  sx={inputSx}
                />
              </Grid>
            </Grid>

            {/* Email */}
            <KDBox mb={2}>
              <KDTypography sx={labelSx}>{t("common.email")}</KDTypography>
              <TextField
                fullWidth
                size="small"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("invite.emailPlaceholder")}
                sx={inputSx}
              />
            </KDBox>

            {/* Send email toggle */}
            {email.trim() && (
              <KDBox mb={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <KDTypography variant="body2" color={darkMode ? "white" : "dark"}>
                      {t("invite.sendEmail")}
                    </KDTypography>
                  }
                />
              </KDBox>
            )}

            {/* Expiry */}
            <KDBox mb={2}>
              <KDTypography sx={labelSx}>
                {t("invite.expiresIn")} — {expiresInDays} {t("invite.days")}
              </KDTypography>
              <Slider
                value={expiresInDays}
                onChange={(_e, val) => setExpiresInDays(val)}
                min={1}
                max={90}
                step={1}
                sx={{
                  color: "primary.main",
                  "& .MuiSlider-thumb": { width: 16, height: 16 },
                }}
              />
            </KDBox>

            {/* Custom message */}
            <KDBox mb={1}>
              <KDTypography sx={labelSx}>{t("invite.message")}</KDTypography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder={t("invite.messagePlaceholder")}
                sx={inputSx}
              />
            </KDBox>

            {/* Error */}
            {error && (
              <KDTypography variant="caption" color="error" mt={1}>
                {error}
              </KDTypography>
            )}
          </>
        ) : (
          /* ── Success State ── */
          <KDBox textAlign="center" py={2}>
            <CheckCircleIcon sx={{ fontSize: 56, color: "#5EEAD4", mb: 2 }} />
            <KDTypography variant="h5" fontWeight="bold" color={darkMode ? "white" : "dark"} mb={1}>
              {t("invite.codeCreated")}
            </KDTypography>

            {/* Code display */}
            <KDBox
              sx={{
                my: 3,
                p: 2.5,
                background: darkMode ? "rgba(13,148,136,0.1)" : "rgba(13,148,136,0.08)",
                border: "2px dashed rgba(94,234,212,0.3)",
                borderRadius: "12px",
              }}
            >
              <KDTypography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: 1 }}
              >
                {t("invite.yourCode")}
              </KDTypography>
              <KDTypography
                variant="h3"
                fontWeight="bold"
                sx={{
                  color: "#5EEAD4",
                  fontFamily: "'Courier New', monospace",
                  letterSpacing: "4px",
                  mt: 0.5,
                }}
              >
                {createdInvite?.code}
              </KDTypography>
            </KDBox>

            {/* Copy buttons */}
            <KDBox display="flex" justifyContent="center" gap={2} mb={3}>
              <KDButton
                variant="outlined"
                size="small"
                onClick={handleCopyCode}
                sx={{
                  color: darkMode ? "white" : "dark",
                  borderColor: darkMode ? "rgba(255,255,255,0.2)" : undefined,
                  borderRadius: "10px",
                  "&:hover": { borderColor: "primary.main" },
                }}
              >
                {codeCopied ? <CheckCircleIcon sx={{ fontSize: 18, mr: 0.5, color: "#5EEAD4" }} /> : <ContentCopyIcon sx={{ fontSize: 18, mr: 0.5 }} />}
                {codeCopied ? t("invite.copied") : t("invite.copyCode")}
              </KDButton>
              <KDButton
                variant="outlined"
                size="small"
                onClick={handleCopyLink}
                sx={{
                  color: darkMode ? "white" : "dark",
                  borderColor: darkMode ? "rgba(255,255,255,0.2)" : undefined,
                  borderRadius: "10px",
                  "&:hover": { borderColor: "primary.main" },
                }}
              >
                {linkCopied ? <CheckCircleIcon sx={{ fontSize: 18, mr: 0.5, color: "#5EEAD4" }} /> : <LinkIcon sx={{ fontSize: 18, mr: 0.5 }} />}
                {linkCopied ? t("invite.copied") : t("invite.copyLink")}
              </KDButton>
            </KDBox>

            {/* Info chips */}
            <KDBox display="flex" justifyContent="center" gap={1} flexWrap="wrap" mb={2}>
              <Chip
                label={`${t("common.role")}: ${createdInvite?.role}`}
                size="small"
                sx={{ bgcolor: "rgba(13,148,136,0.15)", color: "#5EEAD4" }}
              />
              {createdInvite?.accessLevel && (
                <Chip
                  label={`${t("invite.accessLevel")}: ${createdInvite.accessLevel}`}
                  size="small"
                  sx={{ bgcolor: "rgba(139,92,246,0.15)", color: "#C4B5FD" }}
                />
              )}
              <Chip
                label={`${t("invite.expiresIn")}: ${createdInvite?.expiresInDays}d`}
                size="small"
                sx={{ bgcolor: "rgba(234,179,8,0.15)", color: "#FDE68A" }}
              />
              {createdInvite?.emailSentAt && (
                <Chip
                  icon={<EmailIcon sx={{ fontSize: 16 }} />}
                  label={t("invite.emailSent")}
                  size="small"
                  sx={{ bgcolor: "rgba(34,197,94,0.15)", color: "#86EFAC" }}
                />
              )}
            </KDBox>

            {createdInvite?.email && (
              <KDTypography variant="body2" color="text" sx={{ opacity: 0.6 }}>
                {t("invite.sentTo")}: {createdInvite.email}
              </KDTypography>
            )}
          </KDBox>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        {step === "form" ? (
          <>
            <KDButton
              variant="text"
              onClick={handleClose}
              sx={{ color: darkMode ? "rgba(255,255,255,0.5)" : undefined }}
            >
              {t("common.cancel")}
            </KDButton>
            <KDButton
              variant="contained"
              color="primary"
              onClick={handleCreate}
              disabled={creating}
              sx={{
                borderRadius: "10px",
                px: 3,
                background: "linear-gradient(135deg, #0D9488 0%, #5EEAD4 100%)",
                "&:hover": { background: "linear-gradient(135deg, #0B8276 0%, #4DD9C7 100%)" },
              }}
            >
              {creating ? (
                <CircularProgress size={18} sx={{ color: "white", mr: 1 }} />
              ) : (
                <PersonAddIcon sx={{ fontSize: 18, mr: 0.5 }} />
              )}
              {creating ? t("common.loading") : t("invite.createCode")}
            </KDButton>
          </>
        ) : (
          <>
            <KDButton
              variant="outlined"
              onClick={resetForm}
              sx={{
                color: darkMode ? "white" : "dark",
                borderColor: darkMode ? "rgba(255,255,255,0.2)" : undefined,
                borderRadius: "10px",
              }}
            >
              {t("invite.createAnother")}
            </KDButton>
            <KDButton
              variant="contained"
              color="primary"
              onClick={handleClose}
              sx={{
                borderRadius: "10px",
                px: 3,
                background: "linear-gradient(135deg, #0D9488 0%, #5EEAD4 100%)",
              }}
            >
              {t("common.done")}
            </KDButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default InviteModal;
